import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import Holidays from 'date-holidays';
import {
  StyledDatePicker,
  TimeSlotButton,
  StyledBox,
  SectionTitle,
  TimeSlotsGrid,
  ConfirmButton,
} from '../../styles/CustomerStyle/MydatePickerStyle';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { TextField, Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNotAvailableDateContext } from '../../context/NotAvailableDateContext';
import { useNotAvailableTimeContext } from '../../context/NotAvailableTimeContext';
import { fetchNotAvailableTimeSlots } from '../../lib/apiClientAppointment';

const MyDatePicker = ({
  businessId,
  staffId,
  selectedDate,
  onDateChange,
  selectedTime,
  onTimeSelect,
  onConfirmTime,
  totalDuration,
  openingHours
}) => {

  const { t } = useTranslation('myDatePicker');

  const { notAvailableDates, fetchAllNotAvailableDatesByStaff } = useNotAvailableDateContext();
  const { fetchAllNotAvailableTimesByStaff, notAvailableTimes } = useNotAvailableTimeContext();

  const [austrianHolidays, setAustrianHolidays] = useState([]);
  const [notAvailableTimeSlots, setNotAvailableTimeSlots] = useState([]);


  const openingHoursNotFound = openingHours === "NOT_FOUND";
  const IS_SWITZERLAND = businessId === 13;
  const COUNTRY_CODE = IS_SWITZERLAND ? 'CH' : 'AT';
  const TIMEZONE = IS_SWITZERLAND ? 'Europe/Zurich' : 'Europe/Vienna';

  useEffect(() => {
    const hd = new Holidays(COUNTRY_CODE);
    const currentYear = new Date().getFullYear();

    const holidaysThisYear = hd.getHolidays(currentYear);
    const holidaysNextYear = hd.getHolidays(currentYear + 1);

    const allHolidays = [...holidaysThisYear, ...holidaysNextYear];

    const dates = allHolidays.map(h =>
      moment(h.date).format("YYYY-MM-DD")
    );

    setAustrianHolidays(dates);
  }, [COUNTRY_CODE]);

  useEffect(() => {
    if (!staffId) return;

    fetchAllNotAvailableDatesByStaff(businessId, staffId);
    fetchAllNotAvailableTimesByStaff(businessId, staffId);
  }, [
    businessId,
    staffId,
    fetchAllNotAvailableDatesByStaff,
    fetchAllNotAvailableTimesByStaff
  ]);

  useEffect(() => {
    if (!staffId || !selectedDate) return;

    fetchAllNotAvailableTimesByStaff(businessId, staffId);
  }, [
    businessId,
    staffId,
    selectedDate,
    fetchAllNotAvailableTimesByStaff
  ]);

  useEffect(() => {
    if (!staffId || !selectedDate) return;

    const fetchSlots = async () => {
      try {
        const formatted = selectedDate.format("YYYY-MM-DD");
        const slots = await fetchNotAvailableTimeSlots(staffId, formatted);

        if (Array.isArray(slots)) {
          const localSlots = slots.map(s =>
            moment.utc(s).tz(moment.tz.guess()).format("YYYY-MM-DDTHH:mm:ss")
          );
          setNotAvailableTimeSlots(localSlots);
        } else {
          setNotAvailableTimeSlots([]);
        }
      } catch (e) {
        setNotAvailableTimeSlots([]);
      }
    };

    fetchSlots();
  }, [staffId, selectedDate]);


  const parsedOpeningHours = useMemo(() => {
    if (!openingHours || openingHoursNotFound) return { openHour: null, closeHour: null };

    const [openHour] = openingHours.openTime.split(':').map(Number);
    const [closeHour] = openingHours.closeTime.split(':').map(Number);

    return { openHour, closeHour };
  }, [openingHours, openingHoursNotFound]);

  const handleConfirmTime = () => onConfirmTime(selectedTime);
  const handleDateChangeWrapper = (date) => onDateChange(date);
  const handleTimeChangeWrapper = (time) => onTimeSelect(time);

  const generateAllTimeSlots = useCallback(
    (startHour, endHour) => {
      if (!selectedDate || openingHoursNotFound || !openingHours?.openTime) return [];

      const slots = [];
      const startTime = selectedDate.clone().set({ hour: startHour, minute: 0 });
      const endTime = selectedDate.clone().set({ hour: endHour, minute: 0 });

      while (startTime.isBefore(endTime)) {
        slots.push(startTime.clone().format("YYYY-MM-DDTHH:mm:ss"));
        startTime.add(10, "minutes");
      }
      return slots;
    },
    [selectedDate, openingHoursNotFound, openingHours]
  );

  const openingTime = openingHours?.openTime ?? null;
  const closingTime = openingHours?.closeTime ?? null;

  const { amTimeSlots, pmTimeSlots } = useMemo(() => {
    if (!selectedDate || openingHoursNotFound || !openingTime || !closingTime)
      return { amTimeSlots: [], pmTimeSlots: [] };

    const { openHour, closeHour } = parsedOpeningHours;

    return {
      amTimeSlots: generateAllTimeSlots(openHour, Math.min(12, closeHour)),
      pmTimeSlots: generateAllTimeSlots(Math.max(12, openHour), closeHour)
    };
  }, [
    selectedDate,
    openingTime,
    closingTime,
    openingHoursNotFound,
    parsedOpeningHours,
    generateAllTimeSlots
  ]);

  const computedSlots = useMemo(() => {
    const wrap = (slotStrings) =>
      slotStrings.map((s) => ({
        raw: s,
        moment: moment(s)
      }));

    return {
      am: wrap(amTimeSlots),
      pm: wrap(pmTimeSlots)
    };
  }, [amTimeSlots, pmTimeSlots]);

  const closingMoment = useMemo(() => {
    if (!closingTime || openingHoursNotFound || !selectedDate) return null;

    const [h, m] = closingTime.split(":").map(Number);

    return selectedDate.clone().set({
      hour: h,
      minute: m,
      second: 0
    });
  }, [closingTime, selectedDate, openingHoursNotFound]);

  const isWithinNextThreeHours = (slotMoment) => {
    if (openingHoursNotFound) return true;

    const now = moment();
    return (
      selectedDate.isSame(now, "day") &&
      slotMoment.isBetween(
        now,
        now.clone().add(openingHours.minimumAdvanceBookingMinutes / 60, "hours")
      )
    );
  };

  const isNotAvailableTimeSlot = (slot) => {
    if (openingHoursNotFound) return true;

    const slotMoment = slot.moment;
    const now = moment();

    if (isWithinNextThreeHours(slotMoment)) return true;
    if (selectedDate.isSame(now, "day") && slotMoment.isBefore(now)) return true;

    // Block unavailable intervals from context
    const inInterval = notAvailableTimes.some(({ from, to }) => {
      return slotMoment.isBetween(moment(from), moment(to), null, "[)");
    });
    if (inInterval) return true;

    // Block real-time booked slots from API
    const isExactSlotBooked = notAvailableTimeSlots.some(s =>
      moment(s).isSame(slotMoment)
    );
    if (isExactSlotBooked) return true;

    return false;
  };


  const isValidTimeSlot = (slot) => {
    if (openingHoursNotFound || !closingMoment) return false;

    const slotMoment = slot.moment;
    const slotEnd = slotMoment.clone().add(totalDuration, "minutes");

    if (slotEnd.isAfter(closingMoment)) return false;
    if (isWithinNextThreeHours(slotMoment)) return false;

    // check overlap against interval blocks (from context)
    const overlapsInterval = notAvailableTimes.some(({ from, to }) => {
      const fromM = moment(from);
      const toM = moment(to);

      // overlap rule: [start, end) intersects [from, to)
      return slotMoment.isBefore(toM) && slotEnd.isAfter(fromM);
    });

    if (overlapsInterval) return false;

    // existing: check overlap against booked 10-min slots
    const overlapsSlot = notAvailableTimeSlots.some((ts) => {
      const blocked = moment(ts);
      const blockedEnd = blocked.clone().add(10, "minutes");
      return slotEnd.isAfter(blocked) && slotMoment.isBefore(blockedEnd);
    });

    if (overlapsSlot) return false;

    return true;
  };


  const shouldDisableDate = (date) => {
    const unavailable = notAvailableDates.some(({ startDate, endDate }) => {
      const start = moment.utc(startDate).tz(TIMEZONE).startOf("day");
      const end = moment.utc(endDate).tz(TIMEZONE).endOf("day");

      return date.isBetween(start, end, "day", "[]");
    });

    const isHoliday = austrianHolidays.includes(date.format("YYYY-MM-DD"));

    return unavailable || isHoliday;
  };



  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <StyledBox businessId={businessId} sx={{ mt: 2, mb: 4 }}>

        <StyledDatePicker
          businessId={businessId}
          value={selectedDate}
          onChange={handleDateChangeWrapper}
          textField={(params) => (
            <TextField {...params} fullWidth sx={{ fontSize: "1.3rem" }} />
          )}
          disablePast
          shouldDisableDate={shouldDisableDate}
        />

        {openingHoursNotFound && selectedDate && (
          <Box mt={3} textAlign="center">
            <Typography variant="body1" color="error" sx={{ fontWeight: "bold" }}>
              No opening hours available for this date.
            </Typography>
          </Box>
        )}

        {selectedDate && !openingHoursNotFound && (
          <>
            <Box mt={2} textAlign="center">
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {t('selectTimeLabel')}
              </Typography>
            </Box>

            {/* AM SLOTS */}
            {computedSlots.am.length > 0 && (
              <>
                <SectionTitle>{t('morningSectionLabel')}</SectionTitle>
                <TimeSlotsGrid container justifyContent="center">
                  {computedSlots.am.map((slot) => {
                    const available =
                      !isNotAvailableTimeSlot(slot) && isValidTimeSlot(slot);

                    return (
                      <Grid item key={slot.raw}>
                        <TimeSlotButton
                          businessId={businessId}
                          onClick={
                            available ? () => handleTimeChangeWrapper(slot.raw) : null
                          }
                          selected={slot.raw === selectedTime}
                          disabled={!available}
                        >
                          {slot.raw.substring(11, 16)}
                        </TimeSlotButton>
                      </Grid>
                    );
                  })}
                </TimeSlotsGrid>
              </>
            )}

            {/* PM SLOTS */}
            {computedSlots.pm.length > 0 && (
              <>
                <SectionTitle>{t('afternoonSectionLabel')}</SectionTitle>
                <TimeSlotsGrid container justifyContent="center">
                  {computedSlots.pm.map((slot) => {
                    const available =
                      !isNotAvailableTimeSlot(slot) && isValidTimeSlot(slot);

                    return (
                      <Grid item key={slot.raw}>
                        <TimeSlotButton
                          businessId={businessId}
                          onClick={
                            available ? () => handleTimeChangeWrapper(slot.raw) : null
                          }
                          selected={slot.raw === selectedTime}
                          disabled={!available}
                        >
                          {slot.raw.substring(11, 16)}
                        </TimeSlotButton>
                      </Grid>
                    );
                  })}
                </TimeSlotsGrid>
              </>
            )}

            {/* No slots */}
            {computedSlots.am.length === 0 &&
              computedSlots.pm.length === 0 && (
                <Typography textAlign="center" mt={2}>
                  {t('noAvailableTimeSlots')}
                </Typography>
              )}

            <ConfirmButton
              businessId={businessId}
              variant="contained"
              onClick={handleConfirmTime}
              disabled={!selectedTime}
            >
              {t('confirmButtonLabel')}
            </ConfirmButton>
          </>
        )}

      </StyledBox>
    </LocalizationProvider>
  );
};

export default MyDatePicker;
